import React from "react";
import { cn } from "@/lib/utils";
import { getAttendanceSignal } from "@/lib/scheduling";
import iliaMascot from "@/assets/ilia-mascot.png";
import { useI18n } from "@/i18n";

export function IliaMark({ className, size = 20, active = false }) {
  return (
    <span
      className={cn("relative inline-flex shrink-0 items-center justify-center bg-white", className)}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <img
        src={iliaMascot}
        alt=""
        draggable="false"
        className="pointer-events-none h-full w-full select-none object-contain"
      />
      {active ? (
        <span className="absolute bottom-0 right-0 h-[24%] w-[24%] rounded-full bg-success ring-1 ring-white" />
      ) : null}
    </span>
  );
}

export function IliaAvatar({ size = 36, active = false, surface = true, className }) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center",
        surface ? "rounded-lg bg-white p-px" : "bg-transparent",
        className
      )}
      style={{ width: size, height: size }}
    >
      <IliaMark
        size={surface ? size - 2 : size}
        active={active}
        className={surface ? "rounded-md" : "bg-transparent"}
      />
    </span>
  );
}

export function Chip({ children, tone = "muted", className, icon: Icon, ...props }) {
  const tones = {
    muted: "text-muted-foreground",
    ilia: "text-foreground",
    human: "text-foreground",
    primary: "text-foreground",
    accent: "text-foreground",
    success: "text-foreground",
    warning: "text-foreground",
    destructive: "border-destructive/25 bg-destructive-soft/35 text-destructive-soft-foreground",
  };
  return (
    <span {...props} className={cn("inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-0.5 text-xs font-medium shadow-xs", tones[tone], className)}>
      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
      {children}
    </span>
  );
}

const STATUS_DOT = {
  muted: "bg-muted-foreground/60",
  ilia: "bg-ilia",
  human: "bg-primary",
  primary: "bg-primary",
  accent: "bg-accent",
  success: "bg-success",
  warning: "bg-warning",
  destructive: "bg-destructive",
};

export function StatusChip({ children, tone = "muted", emphasis = "default", className, ...props }) {
  const critical = emphasis === "critical" || tone === "destructive";
  return (
    <span
      {...props}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium shadow-xs",
        critical
          ? "border-destructive/25 bg-destructive-soft/35 text-destructive-soft-foreground"
          : "border-border bg-surface text-foreground",
        className
      )}
    >
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", STATUS_DOT[tone] || STATUS_DOT.muted)} aria-hidden="true" />
      {children}
    </span>
  );
}

// Ownership: Ilia vs a named human
export function OwnershipBadge({ owner, className }) {
  const isIlia = owner === "Ilia";
  const { t, localize } = useI18n();
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-0.5 text-xs font-medium text-foreground shadow-xs", className)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", isIlia ? "bg-ilia" : "bg-primary")} />
      {isIlia ? t("lifecycle.ilia") : owner ? localize(owner) : t("ownership.unassigned")}
    </span>
  );
}

const LIFECYCLE = {
  ilia: { labelKey: "lifecycle.ilia", tone: "ilia" },
  handoff_requested: { labelKey: "lifecycle.operatorRequired", tone: "warning" },
  waiting_human: { labelKey: "lifecycle.operatorRequired", tone: "warning" },
  human: { labelKey: "lifecycle.operatorManaged", tone: "primary" },
  waiting_customer: { labelKey: "lifecycle.waitingCustomer", tone: "muted" },
  resolved: { labelKey: "lifecycle.resolved", tone: "success" },
};
export function LifecycleBadge({ status, className }) {
  const s = LIFECYCLE[status] || LIFECYCLE.ilia;
  const { t } = useI18n();
  return <StatusChip tone={s.tone} className={className}>{t(s.labelKey)}</StatusChip>;
}

import { Facebook, Instagram, MessageCircle, MessagesSquare, Send } from "@/components/icons";
const CHANNEL_META = {
  Instagram: { tone: "accent", icon: Instagram, iconClass: "[&_svg]:text-accent" },
  WhatsApp: { tone: "success", icon: MessageCircle, iconClass: "[&_svg]:text-success" },
  Messenger: { tone: "human", icon: MessagesSquare, iconClass: "[&_svg]:text-human" },
  Facebook: { tone: "human", icon: Facebook, iconClass: "[&_svg]:text-human" },
  Telegram: { tone: "human", icon: Send, iconClass: "[&_svg]:text-human" },
};
export function ChannelBadge({ channel, showLabel = true, className }) {
  const m = CHANNEL_META[channel] || { tone: "muted", icon: MessageCircle };
  return (
    <Chip
      tone="muted"
      icon={m.icon}
      className={cn("text-foreground", m.iconClass, !showLabel && "h-6 w-6 justify-center gap-0 p-0", className)}
      aria-label={!showLabel ? channel : undefined}
      title={!showLabel ? channel : undefined}
    >
      {showLabel ? channel : null}
    </Chip>
  );
}
export function ChannelDot({ channel }) {
  const m = CHANNEL_META[channel] || { tone: "muted" };
  const bg = { accent: "bg-accent", success: "bg-success", human: "bg-human", muted: "bg-muted-foreground" }[m.tone];
  return <span className={cn("h-2 w-2 rounded-full", bg)} title={channel} />;
}

const STAGE_TONE = {
  New: "muted", Qualifying: "warning", Qualified: "primary", Offered: "primary", "Booked/Won": "success",
  "Not now": "muted", Lost: "destructive", Disqualified: "destructive",
};
const STAGE_KEYS = {
  New: "stage.new", Qualifying: "stage.qualifying", Qualified: "stage.qualified", Offered: "stage.offered",
  "Booked/Won": "stage.bookedWon", "Not now": "stage.notNow", Lost: "stage.lost", Disqualified: "stage.disqualified",
};
export function StageBadge({ stage, className }) {
  const tone = STAGE_TONE[stage] || "muted";
  const { t } = useI18n();
  return <StatusChip tone={tone} emphasis={tone === "destructive" ? "critical" : "default"} className={className}>{t(STAGE_KEYS[stage] || "stage.new")}</StatusChip>;
}

const BOOKING_STATE = {
  hold: { label: "Temporary hold", labelKey: "booking.status.hold", tone: "warning" },
  pending: { label: "Pending confirmation", labelKey: "booking.status.pending", tone: "warning" },
  confirmed: { label: "Confirmed", labelKey: "booking.status.confirmed", tone: "success" },
  completed: { label: "Attended", labelKey: "booking.status.attended", tone: "success" },
  cancelled: { label: "Cancelled", labelKey: "booking.status.cancelled", tone: "muted" },
  noshow: { label: "No-show", labelKey: "booking.status.noShow", tone: "destructive" },
};
export function BookingStateBadge({ state, className }) {
  const s = BOOKING_STATE[state] || BOOKING_STATE.pending;
  const { t } = useI18n();
  return <StatusChip tone={s.tone} emphasis={s.tone === "destructive" ? "critical" : "default"} className={className}>{t(s.labelKey)}</StatusChip>;
}
export { BOOKING_STATE };

// Provenance: observed / entered / estimate / prediction
const PROV = {
  observed: { labelKey: "provenance.observed", tone: "primary" },
  customer: { labelKey: "provenance.customer", tone: "primary" },
  imported: { labelKey: "provenance.imported", tone: "human" },
  staff: { labelKey: "provenance.staff", tone: "muted" },
  ilia: { labelKey: "provenance.ilia", tone: "ilia" },
  derived: { labelKey: "provenance.derived", tone: "primary" },
  estimate: { labelKey: "provenance.estimate", tone: "warning" },
  prediction: { labelKey: "provenance.prediction", tone: "warning" },
  review: { labelKey: "provenance.review", tone: "warning" },
};
export function SourceTag({ source, className }) {
  const p = PROV[source] || PROV.staff;
  const showDot = p.tone === "warning" || p.tone === "ilia";
  const { t } = useI18n();
  return (
    <span className={cn("inline-flex items-center gap-1 rounded border border-border bg-surface px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground shadow-xs", className)}>
      {showDot ? <span className={cn("h-1.5 w-1.5 rounded-full", p.tone === "warning" ? "bg-warning" : "bg-ilia")} aria-hidden="true" /> : null}
      {t(p.labelKey)}
    </span>
  );
}

export function RiskBadge({ risk, source }) {
  const { t } = useI18n();
  if (source !== "ilia" || risk < 25) return null;
  const high = risk >= 45;
  return (
    <StatusChip tone={high ? "destructive" : "warning"} emphasis={high ? "critical" : "default"}>
      {t("risk.noShow", { risk })}
    </StatusChip>
  );
}

export function AttendanceBadge({ booking, compact = false, className }) {
  const signal = getAttendanceSignal(booking);
  const { t } = useI18n();
  if (!signal) return null;
  return (
    <StatusChip className={className} tone={signal.tone} emphasis={signal.tone === "destructive" ? "critical" : "default"}>
      {signal.probability !== undefined
        ? t(compact ? "attendance.showProbability" : "attendance.likelyToShow", { probability: signal.probability })
        : t(signal.label === "Showed up"
          ? "attendance.showedUp"
          : signal.label === "Did not show"
            ? "attendance.didNotShow"
            : "booking.status.cancelled")}
    </StatusChip>
  );
}
